<template>
  <UiComboboxMultiVeeBase v-bind="{ ...$props, ...$attrs }" />
</template>

<script lang="ts">
import type { PropType } from 'vue'
import type { TValidationRules } from '@team-milva/lab-components'
import { t } from '@/locales'

interface IComboboxOption {
  value: string
  text: string
  subtitle?: string
  isDisabled?: boolean
}

export default {
  name: 'UiComboboxMultiVee',
  inheritAttrs: false,
  props: {
    name: { type: String, required: true as const },
    label: { type: String, default: undefined },
    modelValue: { type: Array as PropType<string[]>, required: true as const },
    options: {
      type: Array as PropType<IComboboxOption[]>,
      required: true as const,
    },
    placeholder: { type: String, default: () => t('W: Vælg...') },
    searchPlaceholder: { type: String, default: () => t('W: Søg...') },
    emptyText: { type: String, default: () => t('W: Ingen resultater') },
    closeOnSelect: { type: Boolean, default: undefined },
    rules: { type: Array as PropType<TValidationRules[]>, default: undefined },
    isRequired: { type: Boolean, default: undefined },
    showAsRequired: { type: Boolean, default: undefined },
    isDisabled: { type: Boolean, default: undefined },
    isAddOnly: { type: Boolean, default: undefined },
  },
}
</script>

<style>
@reference "../../globals/css/base.css";
.s-combobox {
  .s-combobox__trigger {
    @apply rounded-md border border-border-subtle bg-surface;

    &:hover:not([aria-disabled="true"]) {
      @apply border-border-strong;
    }

    &:focus-visible {
      @apply border-border-focus outline-none;
      outline: 2px solid var(--color-border-focus);
      outline-offset: 1px;
    }
  }

  &.s-combobox--open .s-combobox__trigger {
    @apply border-border-focus;
    outline: 2px solid var(--color-border-focus);
    outline-offset: 1px;
  }

  .s-combobox__placeholder {
    @apply text-text-muted;
  }

  .s-combobox__tag {
    @apply bg-surface-muted border border-border-subtle text-text-primary;
  }

  .s-combobox__tag-remove {
    @apply text-text-muted hover:text-text-primary;
  }

  .s-combobox__dropdown {
    @apply rounded-md border border-border-subtle bg-surface shadow-md;
  }

  .s-combobox__search-wrap {
    @apply border-b border-border-subtle;
  }

  .s-combobox__empty {
    @apply text-text-muted;
  }

  .s-combobox__option {
    background: none;
    border: none;

    &:hover:not(.s-combobox__option--disabled),
    &.s-combobox__option--highlighted {
      @apply bg-surface-muted;
    }

    &.s-combobox__option--selected {
      @apply bg-action-soft-bg font-medium text-action-soft-text;
    }

    &.s-combobox__option--disabled {
      @apply text-text-muted;
    }
  }

  .s-combobox__option-check {
    @apply text-action;
  }

  &.s-combobox--disabled {
    .s-combobox__trigger {
      @apply cursor-not-allowed bg-surface-muted text-text-muted;
    }

    .s-combobox__tag-remove {
      @apply pointer-events-none opacity-50;
    }
  }
}
</style>
